// components/LegalRepresentatives/RepresentativesTable.js

import { formatDate } from "../service/utils/formatters";

/**
 * Composant pour afficher le tableau des représentants légaux
 * @param {Object} props - Propriétés du composant
 * @param {Array} props.representatives - Liste des représentants
 * @param {Function} props.onViewManagedAccounts - Fonction pour voir les comptes gérés
 */
const RepresentativesTable = ({ representatives, onViewManagedAccounts }) => {

  /**
   * Obtenir la classe CSS pour le badge de statut
   * @param {string} status - Statut du représentant
   * @returns {string} Classes CSS pour le badge
   */
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'actif':
        return 'bg-success bg-opacity-10 text-success border border-success border-opacity-25';
      case 'inactif':
        return 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25';
      case 'en attente':
        return 'bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25';
      default:
        return 'bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25';
    }
  };

  // Si aucun représentant n'est trouvé
  if (representatives.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-users fa-3x text-muted mb-3"></i>
        <h5 className="text-muted">Aucun représentant trouvé</h5>
        <p className="text-muted">
          Aucun représentant légal ne correspond à vos critères de recherche.
        </p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-striped align-middle">
        <thead className="table-light">
          <tr>
            <th>Nom Complet</th>
            <th>Email</th>
            <th>Type</th>
            <th className="text-center">Comptes Gérés</th>
            <th>Dernier Mandat</th>
            <th>Date Création</th>
            <th>Statut</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {representatives.map((representative) => (
            <tr key={representative.accountId}>
              {/* Nom complet */}
              <td>
                <div className="fw-semibold">{representative.displayName}</div>
                {/* <small className="text-muted">ID: {representative.accountId}</small> */}
              </td>
              
              {/* Email */}
              <td>
                <a
                  href={`mailto:${representative.email}`}
                  className="text-decoration-none"
                  title="Envoyer un email"
                >
                  {representative.email}
                </a>
              </td>
              
              {/* Type de compte */}
              <td>
                <span className="badge bg-info bg-opacity-10 text-info">
                  {representative.typeName}
                </span>
              </td>
              
              {/* Nombre de comptes gérés */}
              <td className="text-center">
                <div className="d-flex flex-column align-items-center">
                  <span className="fw-bold text-primary fs-6">
                    {representative.managedAccountsCount}
                  </span>
                  <small className="text-muted">
                    {representative.activeMandatesCount} actif(s)
                  </small>
                </div>
              </td>
              
              {/* Dernier mandat */}
              <td>
                <div>
                  <div className="small fw-semibold">{representative.lastMandateType}</div>
                  <small className="text-muted">
                    {formatDate(representative.lastMandateDate)}
                  </small>
                </div>
              </td>
              
              {/* Date de création */}
              <td>
                <small className="text-muted">
                  {formatDate(representative.creationDate)}
                </small>
              </td>
              
              {/* Statut */}
              <td>
                <span className={`badge ${getStatusBadgeClass(representative.status)}`}>
                  {representative.status}
                </span>
              </td>
              
              {/* Actions */}
              <td className="text-center">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onViewManagedAccounts(representative)}
                  title="Voir les comptes gérés"
                  disabled={representative.managedAccountsCount === 0}
                >
                  <i className="fas fa-eye me-1"></i>
                  Détails
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RepresentativesTable;